import { useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import { observer } from "mobx-react"
import { Button, Form, Input, List, Select, Typography } from "antd"
import Table, { ColumnsType } from "antd/es/table"

import { authStore } from "@src/app/authStore"
import { balancesStore } from "@src/app/balanceStore"
import { bankAccountsStore } from "@src/app/bankAccountsStore"
import { ModalOtp } from "@src/app/components/ModalOtp"
import { Navigation } from "@src/app/components/navigation"
import { notify } from "@src/app/components/notify"
import { paginationConfig } from "@src/app/configs"
import { catchErrors } from "@src/shared/api"
import { FiatWithdrawalResponse } from "@src/shared/api/generated"
import { dateTimeFormat } from "@src/shared/date"
import { getFees } from "./getFees"
import css from "./index.module.css"
import { FormData, WithdrawFiatStore } from "./store"
import { WithdrawalInfo } from "./withdrawalInfo"

const { Item } = Form

const { Paragraph } = Typography

const { Item: ListItem } = List

const columns: ColumnsType<FiatWithdrawalResponse> = [
  {
    dataIndex: "id",
    title: "ID",
  },
  {
    dataIndex: "created_at",
    render: (_, { created_at }) => dateTimeFormat(created_at as string),
    title: "Date",
  },
  {
    dataIndex: "bank_account_name",
    title: "Name",
  },
  {
    dataIndex: "amount",
    render: (_, { amount, bank_account_currency }) =>
      `${amount} ${bank_account_currency}`,
    title: "Amount",
  },
  {
    dataIndex: "status",
    title: "Status",
  },
]

export const WithdrawFiatPage = observer(() => {
  const [store] = useState(() => new WithdrawFiatStore())

  const [disabledSubmit, setDisabledSubmit] = useState(true)

  const [isOtpOpen, setIsOtpOpen] = useState(false)

  const [otpValue, setOtpValue] = useState<Array<string>>([])

  const [disabledOtpSubmit, setDisabledOtpSubmit] = useState(true)

  const [isInfoOpen, setIsInfoOpen] = useState(false)

  const [fees, setFees] = useState<{
    [key: string]: string
  }>({})

  const { network, symbol } = useParams()

  const [form] = Form.useForm()

  const { balances } = balancesStore

  const {
    bankAccountsOptions,
    chooseBankAccount,
    chosenBankAccount,
    isLoading,
    resetBankAccount,
  } = bankAccountsStore

  const { merchant } = authStore

  const {
    addFormData,
    addOtp,
    cancelWithdrawal,
    createWithdrawal,
    loadWithdrawalsList,
    pageSize,
    sendWithdrawal,
    total,
    withdrawalInfo,
    withdrawalsList,
  } = store

  const balance = useMemo(
    () =>
      balances?.find(
        (item) => item.symbol === symbol && item.network === network,
      ),
    [balances, symbol],
  )

  const setMaxAmount = () => {
    form.setFieldsValue({ amount: balance?.payouts_available?.withdrawal })

    form.validateFields(["amount"])
  }

  const handleFormChange = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length)

    const { amount, bank_account_id } = form.getFieldsValue()

    const isValid = Boolean(bank_account_id) && Boolean(amount) && amount > 0

    setDisabledSubmit(hasErrors || !isValid)
  }

  const onFormSubmit = (values: FormData) => {
    addFormData(values)

    setIsOtpOpen(true)
  }

  const onOtpCancel = () => {
    setIsOtpOpen(false)
  }

  const onOtpChange = (otp: string[]) => {
    setOtpValue(otp)

    setDisabledOtpSubmit(otp.length !== 6)
  }

  const onOtpSubmit = (value: { otp: string[] }) => {
    setDisabledOtpSubmit(true)

    addOtp(value.otp)

    createWithdrawal()
      .then(() => {
        setIsInfoOpen(true)

        setIsOtpOpen(false)
      })
      .catch(catchErrors)
  }

  const onSendWithdrawal = () => {
    sendWithdrawal(withdrawalInfo!.id!)
      .then((res) => {
        if (res.success) {
          notify.success("Withdrawal has been sent")
        }
      })
      .catch(catchErrors)
      .finally(() => {
        setIsInfoOpen(false)

        form.resetFields()

        resetBankAccount()

        setFees({})
      })
  }

  const onCancelWithdrawal = () => {
    cancelWithdrawal(withdrawalInfo!.id!)
      .then((res) => {
        if (res.success) {
          notify.success("Withdrawal has been canceled")
        }
      })
      .catch(catchErrors)
      .finally(() => {
        setIsInfoOpen(false)

        form.resetFields()

        resetBankAccount()

        setFees({})
      })
  }

  useEffect(() => resetBankAccount(), [])

  useEffect(() => {
    if (merchant && chosenBankAccount) {
      const fee = getFees(
        merchant.fiat_withdrawal_transfers,
        chosenBankAccount!.transfer!.toLocaleLowerCase(),
      )

      setFees(fee)
    }
  }, [chosenBankAccount, merchant])

  return (
    <div className={css.container}>
      <Navigation title="Withdraw" />
      <div className={css.accountWrapper}>
        <Form
          className={css.form}
          form={form}
          id="GENERATED_ID_WITHDRAWFIAT_FORM_VOID_1"
          layout="vertical"
          name="withdrawFiat"
          onFieldsChange={handleFormChange}
          onFinish={onFormSubmit}
          size="large"
        >
          <Paragraph>Select the required details for withdrawal</Paragraph>
          <Item
            label="Bank account"
            name="bank_account_id"
            rules={[{ message: "Please choose bank account", required: true }]}
          >
            <Select
              className={css.select}
              id="GENERATED_ID_WITHDRAWFIAT_SELECT_VOID_1"
              onChange={chooseBankAccount}
              options={bankAccountsOptions}
              placeholder="Select one of your bank accounts from the list"
              size="large"
            ></Select>
          </Item>
          <Item
            label="Amount"
            name="amount"
            rules={[
              {
                message: `Maximum withdrawal amount: ${balance?.payouts_available?.withdrawal}`,
                required: true,
                validator: (_, value) => {
                  if (
                    value <= Number(balance?.payouts_available?.withdrawal) &&
                    value.match(/^(?!0{2,})\d+(\.\d{1,2})?$/)
                  ) {
                    return Promise.resolve()
                  }

                  return Promise.reject(
                    new Error("Please input correct amount"),
                  )
                },
              },
            ]}
          >
            <Input
              addonAfter={
                <Button
                  id="GENERATED_ID_WITHDRAWFIAT_BUTTON_MAX_1"
                  onClick={setMaxAmount}
                  size="middle"
                  type="link"
                >
                  MAX
                </Button>
              }
              placeholder="0"
            />
          </Item>
          <Paragraph>
            <span style={{ color: "#949597" }}>Available balance:</span>{" "}
            {balance?.payouts_available?.withdrawal} {symbol}
          </Paragraph>
          {Object.keys(fees).length > 0 && (
            <Paragraph>
              <span style={{ color: "#949597" }}>Commission:</span>{" "}
              {fees.percent}% +{fees.fix} {symbol}
            </Paragraph>
          )}
          <Item>
            <Button
              disabled={disabledSubmit}
              htmlType="submit"
              id="GENERATED_ID_WITHDRAWFIAT_BUTTON_REQUEST_1"
              size="large"
              type="primary"
            >
              Request
            </Button>
          </Item>
        </Form>
        <List className={css.list}>
          <Paragraph style={{ fontWeight: 700 }}>Bank Info</Paragraph>
          <ListItem>
            <div>Account Name:</div>
            <div className={css.listValue}>
              {chosenBankAccount?.details?.beneficiary_account_name}
            </div>
          </ListItem>
          <ListItem>
            <div>IBAN:</div>
            <div className={css.listValue}>
              {chosenBankAccount?.details?.beneficiary_account_number}
            </div>
          </ListItem>
          <ListItem>
            <div>BIC:</div>
            <div className={css.listValue}>
              {chosenBankAccount?.details?.bic_swift_code}
            </div>
          </ListItem>
          <ListItem>
            <div>Bank Name:</div>
            <div className={css.listValue}>
              {chosenBankAccount?.details?.bank_name}
            </div>
          </ListItem>
          <ListItem>
            <div>Bank Address:</div>
            <div className={css.listValue}>
              {chosenBankAccount?.details?.bank_address}
            </div>
          </ListItem>
        </List>
      </div>
      {
        (withdrawalsList.length > 0,
        (
          <Table
            columns={columns}
            dataSource={withdrawalsList}
            expandable={{
              expandedRowRender: (record) => (
                <ListExpandable isLoading={isLoading} withdraw={record} />
              ),
            }}
            loading={isLoading}
            pagination={{
              onChange: (page) => {
                const offset = (page - 1) * pageSize

                loadWithdrawalsList(offset)
              },
              pageSize,
              total,
              ...paginationConfig,
            }}
            style={{ width: "100%" }}
          />
        ))
      }
      <ModalOtp
        isDisabled={disabledOtpSubmit}
        isOpen={isOtpOpen}
        onCancel={onOtpCancel}
        onChange={onOtpChange}
        onSubmit={onOtpSubmit}
        value={otpValue}
      />
      <WithdrawalInfo
        info={withdrawalInfo!}
        onCancel={() => onCancelWithdrawal()}
        onSubmit={() => onSendWithdrawal()}
        open={isInfoOpen}
      />
    </div>
  )
})
function ListExpandable({
  isLoading,
  withdraw,
}: {
  isLoading: boolean
  withdraw: FiatWithdrawalResponse
}) {
  const {
    amount,
    bank_account_currency,
    bank_account_details,
    bank_account_name,
    created_at,
    id,
    status,
    total_amount,
  } = withdraw

  return (
    <List loading={isLoading}>
      <ListItem className={css.listItem}>
        <div className={css.listTitle}>ID</div>
        <div>{id}</div>
      </ListItem>
      <ListItem className={css.listItem}>
        <div className={css.listTitle}>Date</div>
        <div>{dateTimeFormat(created_at as string)}</div>
      </ListItem>
      <ListItem className={css.listItem}>
        <div className={css.listTitle}>Name</div>
        <div>{bank_account_name}</div>
      </ListItem>
      <ListItem className={css.listItem}>
        <div className={css.listTitle}>Amount</div>
        <div>{`${amount} (${bank_account_currency})`}</div>
      </ListItem>
      <ListItem className={css.listItem}>
        <div className={css.listTitle}>Total amount</div>
        <div>{`${total_amount} (${bank_account_currency})`}</div>
      </ListItem>
      <ListItem className={css.listItem}>
        <div className={css.listTitle}>Status</div>
        <div>{status}</div>
      </ListItem>
      <ListItem className={css.listItem}>
        <div className={css.listTitle}>Beneficiary account name</div>
        <div>{bank_account_details?.beneficiary_account_name}</div>
      </ListItem>
      <ListItem className={css.listItem}>
        <div className={css.listTitle}>Beneficiary address</div>
        <div>{bank_account_details?.beneficiary_address}</div>
      </ListItem>
      <ListItem className={css.listItem}>
        <div className={css.listTitle}>Beneficiary IBAN /Account number</div>
        <div>{bank_account_details?.beneficiary_account_number}</div>
      </ListItem>
      <ListItem className={css.listItem}>
        <div className={css.listTitle}>Bank name</div>
        <div>{bank_account_details?.bank_name}</div>
      </ListItem>
      <ListItem className={css.listItem}>
        <div className={css.listTitle}>Bank address</div>
        <div>{bank_account_details?.bank_address}</div>
      </ListItem>
      <ListItem className={css.listItem}>
        <div className={css.listTitle}>BIC/ SWIFT code</div>
        <div>{bank_account_details?.bic_swift_code}</div>
      </ListItem>
    </List>
  )
}
